import type { FC } from "react";

interface FieldDefectToolProps {
	mode: "tunnel" | "curtain";
	onModeChange: (mode: "tunnel" | "curtain") => void;
	hasDefect: boolean;
	curtainAwaitingSecondTap: boolean;
	onFlipSide: () => void;
	onClear: () => void;
	// Tunnel reuses the polygon ROI engine.
	regionDrawMode: "none" | "drawing";
	regionPointCount: number;
	onStartTunnelDraw: () => void;
	onUndoPoint: () => void;
	onCancelDrawing: () => void;
}

const modeBtn = (active: boolean) =>
	`flex-1 min-h-11 rounded px-3 py-2 text-sm font-medium ${
		active ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
	}`;

const actionBtn = "w-full min-h-11 rounded px-3 py-2 text-sm";
// Half-width variant for buttons that sit in a flex-gap row.
const rowBtn = "flex-1 min-h-11 rounded px-3 py-2 text-sm";

export const FieldDefectTool: FC<FieldDefectToolProps> = ({
	mode,
	onModeChange,
	hasDefect,
	curtainAwaitingSecondTap,
	onFlipSide,
	onClear,
	regionDrawMode,
	regionPointCount,
	onStartTunnelDraw,
	onUndoPoint,
	onCancelDrawing,
}) => {
	const drawing = regionDrawMode === "drawing";

	return (
		<div className="p-4 space-y-3">
			<div className="mb-2 font-semibold">Field Defect</div>
			<p className="text-sm text-gray-600">
				Black out part of the visual field. <strong>Curtain</strong> blacks one
				side of a line; <strong>Tunnel</strong> blacks everything outside a
				drawn shape.
			</p>

			<div className="flex gap-2">
				<button
					type="button"
					className={modeBtn(mode === "curtain")}
					onClick={() => onModeChange("curtain")}
				>
					Curtain
				</button>
				<button
					type="button"
					className={modeBtn(mode === "tunnel")}
					onClick={() => onModeChange("tunnel")}
				>
					Tunnel
				</button>
			</div>

			{mode === "curtain" ? (
				<div className="space-y-2">
					<p className="rounded border border-dashed border-gray-300 p-3 text-sm text-gray-500">
						{curtainAwaitingSecondTap
							? "Tap a second point to set the line."
							: hasDefect
								? "Curtain set. Use Flip side or Clear."
								: "Tap two points on the image to draw the dividing line."}
					</p>
					{hasDefect && (
						<button
							type="button"
							className={`${actionBtn} bg-blue-500 text-white`}
							onClick={onFlipSide}
						>
							Flip side
						</button>
					)}
				</div>
			) : (
				<div className="space-y-2">
					{drawing ? (
						<>
							<p className="rounded border border-dashed border-gray-300 p-3 text-sm text-gray-500">
								Tap to add points. Double-tap or tap the first point to close
								(min 3).
							</p>
							<div className="flex gap-2">
								<button
									type="button"
									className={`${rowBtn} bg-gray-200 text-gray-700`}
									onClick={onUndoPoint}
									disabled={regionPointCount === 0}
								>
									Undo point
								</button>
								<button
									type="button"
									className={`${rowBtn} bg-gray-200 text-gray-700`}
									onClick={onCancelDrawing}
								>
									Cancel
								</button>
							</div>
						</>
					) : (
						<button
							type="button"
							className={`${actionBtn} bg-blue-500 text-white`}
							onClick={onStartTunnelDraw}
						>
							{hasDefect ? "Redraw shape" : "Draw shape"}
						</button>
					)}
				</div>
			)}

			{hasDefect && (
				<button
					type="button"
					className={`${actionBtn} bg-red-500 text-white transition-colors hover:bg-red-600`}
					onClick={onClear}
				>
					Clear
				</button>
			)}
		</div>
	);
};
